import React, { useCallback, useEffect, useState } from 'react';

import {
  Container,
  ContainerButton,
  AddPacienteButton,
  InputObs,
  BotaoExcluir,
  BotaoLimpar,
  BotaoSalvar,
  BotoesContainer,
  InputData,
  CirculoCarregandoDiv,
  CirculoCarregando,
} from './styles';
import { FiPlus, FiSave, FiRefreshCw, FiTrash2 } from 'react-icons/fi';

import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { useIndexedDB } from '../../context/IndexedDBContext';
import { useConfigs } from '../../context/ConfigsContext';
import { returnAxiosError } from '../../utils/returnAxiosError';

import ComboboxComFiltro from '../../components/autocomplete';
import Dialog from '../../components/dialog';
import Paciente from '../Paciente';
import ConfirmationModal from '../../components/confirmationModal';

import IPaciente from '../../routes/vms/IPaciente';
import ICategoria from '../../routes/vms/ICategoria';
import IProfissional from '../../routes/vms/IProfissional';
import IAgendamento from '../../routes/vms/IAgendamento';
import IListaEspera from '../../routes/vms/IListaEspera';
import { adicionarMinutos } from '../../utils/adicionarMinutos';
import ILocal from '../../routes/vms/ILocal';

interface HorariosCombo {
  id: string;
  nome: string;
}

interface AgendamentoProps {
  onClose: () => void;
  agendamentoParametro: IAgendamento | undefined;
  esperaParametro?: IListaEspera;
  profissionalParametro: string;
  dataParametro: Date;
  horaParametro: string;
}

const Agendamento: React.FC<AgendamentoProps> = ({
  agendamentoParametro,
  esperaParametro = undefined,
  profissionalParametro,
  dataParametro,
  horaParametro,
  onClose,
}) => {
  const { user, verifyAccess } = useAuth();
  const { addToast } = useToast();
  const { lerIndexedDB, excluirIndexedDB } = useIndexedDB();
  const { lerConfig } = useConfigs();

  const [paciente, setPaciente] = useState<string | null>(null);
  const [pacienteErro, setPacienteErro] = useState<string>('');
  const [pacientes, setPacientes] = useState<IPaciente[]>([]);

  const [intervalo, setIntervalo] = useState('');

  const [profissional, setProfissional] = useState<string | null>(
    profissionalParametro,
  );
  const [profissionalErro, setProfissionalErro] = useState<string>('');
  const [profissionais, setProfissionais] = useState<IProfissional[]>([]);

  const [data, setData] = useState('');
  const [dataErro, setDataErro] = useState('');

  const [categoria, setCategoria] = useState<string | null>(null);
  const [categoriaErro, setCategoriaErro] = useState<string>('');
  const [categorias, setCategorias] = useState<ICategoria[]>([]);

  const [local, setLocal] = useState<string | null>(null);
  const [localErro, setLocalErro] = useState<string>('');
  const [locais, setLocais] = useState<{ id: string; nome: string }[]>([]);

  const [horaIni, setHoraIni] = useState<string | null>(null);
  const [horaIniErro, setHoraIniErro] = useState<string>('');

  const [horaFim, setHoraFim] = useState<string | null>(null);
  const [horaFimErro, setHoraFimErro] = useState<string>('');

  const [horarios, setHorarios] = useState<HorariosCombo[]>([]);

  const [obs, setObs] = useState<string>('');
  const [isModalOpen, setModalOpen] = useState(false);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [agendado, setAgendado] = useState(false);
  const [loading, setLoading] = useState(false);
  const [motivoExclusao, setMotivoExclusao] = useState<string>('');
  const [alteraTodosOpen, setAlteraTodosOpen] = useState(false);
  const [excluiTodosOpen, setExcluiTodosOpen] = useState(false);

  const atualizaDados = useCallback(async () => {
    try {
      setLoading(true);

      const [
        categoriasResponse,
        pacientesResponse,
        profissionaisResponse,
        locaisResponse,
      ] = await Promise.all([
        api.get<ICategoria[]>('/categoria/lista'),
        api.get<IPaciente[]>(`/pacientes/${esperaParametro ? 'todos' : ''}`),
        api.get<IProfissional[]>(
          `/users/profissionais?empresa_id=${user.empresa_id}`,
        ),
        api.get<ILocal[]>('/local/lista'),
      ]);

      setCategorias(categoriasResponse.data);
      setPacientes(pacientesResponse.data);
      setProfissionais(profissionaisResponse.data);
      setLocais(
        locaisResponse.data.map(local => {
          return { id: local.id, nome: local.descricao };
        }),
      );

      setLoading(false);
    } catch (error) {
      console.error('Erro ao buscar dados:', error);
    }
  }, [user.empresa_id, esperaParametro]);

  const defineHorarios = useCallback(async () => {
    const horariosArray = await api.get<{ horarios: string[] }>(
      `/empresas/horarios/`,
      {
        params: {
          url_frontend: user.empresa.url_frontend,
        },
      },
    );

    const horariosCombo: HorariosCombo[] = horariosArray.data.horarios.map(
      horario => ({
        id: horario,
        nome: horario,
      }),
    );

    setHorarios(horariosCombo);
  }, [user]);

  const handleOpenDialog = () => setDialogOpen(true);

  const handleCloseDialog = useCallback(async () => {
    setDialogOpen(false);

    lerIndexedDB<string>(
      `${user.empresa.url_frontend}:id-paciente-gravado`,
    ).then(idIndexedDB => {
      if (idIndexedDB !== null) {
        setPaciente(String(idIndexedDB));
        atualizaDados();

        excluirIndexedDB(`${user.empresa.url_frontend}:id-paciente-gravado`);
      }
    });
  }, [atualizaDados, excluirIndexedDB, lerIndexedDB, user]);

  const aoAlterarPaciente = (novoPaciente: string | null) => {
    setPaciente(novoPaciente);
    setPacienteErro('');
  };

  const aoAlterarData = (event: React.ChangeEvent<HTMLInputElement>) => {
    setData(event.target.value);
    setDataErro('');
  };

  const aoAlterarProfissional = (novoProfissional: string | null) => {
    setProfissional(novoProfissional);
    setProfissionalErro('');
  };

  const aoAlterarLocal = useCallback(async (novoLocal: string | null) => {
    setLocal(novoLocal);
    setLocalErro('');
  }, []);

  const aoAlterarCategoria = useCallback(
    async (novaCategoria: string | null) => {
      setCategoria(novaCategoria);
      setCategoriaErro('');

      const horariosArray = await api.get<{
        horarios: string[];
        metade_intervalo: boolean;
      }>(`/empresas/horarios/`, {
        params: {
          url_frontend: user.empresa.url_frontend,
          categoria_id: novaCategoria,
        },
      });

      const horariosCombo: HorariosCombo[] = horariosArray.data.horarios.map(
        horario => ({
          id: horario,
          nome: horario,
        }),
      );

      setHorarios(horariosCombo);

      setHoraFim(
        adicionarMinutos(
          horaIni,
          horariosArray.data.metade_intervalo
            ? String(Number(intervalo) / 2)
            : intervalo,
        ),
      );
    },
    [user.empresa.url_frontend, horaIni, intervalo],
  );

  const aoAlterarHoraIni = useCallback(
    async (novoHorario: string | null) => {
      setHoraIni(novoHorario);

      try {
        const metade_intervalo = await api.get(
          `/categoria/${categoria ? categoria : ''}`,
        );

        setHoraFim(
          adicionarMinutos(
            novoHorario,
            metade_intervalo.data ? String(Number(intervalo) / 2) : intervalo,
          ),
        );
      } catch (error) {
        setHoraFim(adicionarMinutos(novoHorario, intervalo));
      }

      setHoraIniErro('');
    },
    [categoria, intervalo],
  );

  const aoAlterarHoraFim = (novoHorario: string | null) => {
    setHoraFim(novoHorario);
    setHoraFimErro('');
  };

  const aoAlterarObs = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setObs(event.target.value);
    },
    [],
  );

  const validacoesAgendamento = useCallback(() => {
    setProfissionalErro('');
    setHoraIniErro('');
    setHoraFimErro('');
    setPacienteErro('');
    setCategoriaErro('');
    setDataErro('');
    setLocalErro('');

    let erroValidacao = false;
    if (!profissional) {
      setProfissionalErro('Informação obrigatória!');
      erroValidacao = true;
    }

    if (esperaParametro) {
      if (!data) {
        setDataErro('Informação obrigatória!');
        erroValidacao = true;
      }
    }

    if (!horaIni) {
      setHoraIniErro('Informação obrigatória!');
      erroValidacao = true;
    }

    if (!horaFim) {
      setHoraFimErro('Informação Obrigatória!');
      erroValidacao = true;
    }

    if (!paciente) {
      setPacienteErro('Informação obrigatória!');
      erroValidacao = true;
    }

    if (!categoria) {
      setCategoriaErro('Informação obrigatória!');
      erroValidacao = true;
    }

    if (lerConfig('LOCAL OBRIGATÓRIO PARA AGENDAMENTOS') === 'SIM') {
      if (!local) {
        setLocalErro('Informação obrigatória!');
        erroValidacao = true;
      }
    }

    if (erroValidacao) {
      addToast({
        type: 'info',
        title: 'Pendências na validação',
        description:
          'Efetue as correções indicadas em cada campo e tente novamente.',
      });
      return false;
    }
  }, [
    addToast,
    profissional,
    horaIni,
    horaFim,
    paciente,
    local,
    categoria,
    data,
    lerConfig,
    esperaParametro,
  ]);

  const aoLimpar = useCallback(() => {
    setPaciente(null);
    setCategoria(null);
    setObs('');
  }, []);

  const handleExcluirAgendamento = useCallback(
    async (motivo: string) => {
      if (!agendamentoParametro) {
        addToast({
          type: 'error',
          title: 'Erro ao excluir',
          description: 'Nenhum agendamento selecionado para exclusão',
        });

        return;
      }

      if (motivo === '') {
        addToast({
          type: 'error',
          title: 'Erro ao excluir',
          description: 'Motivo não informado',
        });

        return;
      }

      if (!agendamentoParametro.lote) {
        try {
          setLoading(true);

          await api.post(
            `/agendamentos/excluir/${agendamentoParametro?.id}/${false}`,
            {
              motivo,
              data: agendamentoParametro?.data_criacao,
            },
          );

          setLoading(false);
        } catch (error) {
          addToast(returnAxiosError(error));

          return;
        }

        addToast({
          type: 'success',
          title: 'Removido',
          description: 'Agendamento excluido',
        });

        setTimeout(() => onClose(), 1000);
      } else {
        if (
          verifyAccess('PERGUNTA SE QUER EXCLUIR UMA OU TODAS AS RECORRÊNCIAS')
        ) {
          setExcluiTodosOpen(true);
        } else {
          try {
            setLoading(true);

            await api.post(
              `/agendamentos/excluir/${agendamentoParametro?.id}/${false}`,
              {
                motivo: motivoExclusao.toUpperCase(),
                data: agendamentoParametro?.data_criacao,
              },
            );

            setLoading(false);
          } catch (error) {
            addToast(returnAxiosError(error));

            return;
          }

          addToast({
            type: 'success',
            title: 'Removido',
            description: 'Agendamento excluido',
          });

          setTimeout(() => onClose(), 1000);
        }
      }

      setModalOpen(false);
    },
    [addToast, agendamentoParametro, onClose, motivoExclusao, verifyAccess],
  );

  const aoSalvar = useCallback(async () => {
    if (esperaParametro) {
      try {
        if (esperaParametro.ordem !== 1) {
          if (
            !verifyAccess(
              'AGENDAR ALGUÉM DA LISTA DE ESPERA QUE NÃO SEJA O PRIMEIRO',
            )
          ) {
            addToast({
              title: 'Permissão',
              description:
                'Acesso negado. Seu perfil de usuario não tem permissão para executar essa ação',
              type: 'error',
            });

            return;
          }
        }

        await api.get(
          `/pacientes/valida-espera/${esperaParametro.paciente.id}`,
        );
      } catch (error) {
        addToast({
          title: 'Validação',
          description: 'Erro de validação. Corrija no cadastro para prosseguir',
          type: 'error',
        });

        setDialogOpen(true);

        return;
      }

      if (esperaParametro.paciente.tipo === 'ESPERA') {
        await api.patch(`/pacientes/define/${esperaParametro.paciente.id}`);
      }
    }

    if (validacoesAgendamento() === false) {
      return;
    }

    let ano;
    let mes;
    let dia;

    let dataFormatada;

    if (esperaParametro) {
      const dataISO = new Date(data);
      dataISO.setHours(dataISO.getHours() + 3);

      ano = dataISO.getFullYear();
      mes = String(dataISO.getMonth() + 1).padStart(2, '0'); // getMonth() retorna de 0 a 11
      dia = String(dataISO.getDate()).padStart(2, '0');
      dataFormatada = `${ano}-${mes}-${dia}`;
    } else {
      ano = dataParametro.getFullYear();
      mes = String(dataParametro.getMonth() + 1).padStart(2, '0'); // getMonth() retorna de 0 a 11
      dia = String(dataParametro.getDate()).padStart(2, '0');
      dataFormatada = `${ano}-${mes}-${dia}`;
    }

    try {
      if (agendamentoParametro) {
        const agendamentoAlterar = {
          categoria_id: categoria,
          hora: horaIni,
          observacoes: obs,
          data_criacao: dataFormatada,
          profissional_id: profissional,
          paciente_id: paciente,
          inutilizado: false,
          local_id: local,
        };

        if (agendamentoParametro.lote) {
          setAlteraTodosOpen(true);
        } else {
          await api.put(
            `/agendamentos/${agendamentoParametro.id}`,
            agendamentoAlterar,
          );

          addToast({
            type: 'success',
            title: 'Parabéns',
            description: 'Agendamento cadastrado com sucesso',
          });

          onClose();
        }
      } else {
        const agendamentoGravar: Omit<IAgendamento, 'id' | 'inutilizado'> = {
          horaIni,
          horaFim,
          categoria_id: categoria,
          paciente_id: paciente,
          profissional_id: profissional ? profissional : '',
          observacoes: obs.toUpperCase(),
          data_criacao: dataFormatada ?? '',
          local_id: local,
        };

        await api.post('/agendamentos', agendamentoGravar);

        if (esperaParametro) {
          await api.post(`/espera/excluir/${esperaParametro.id}`, {
            motivo: `PACIENTE AGENDADO NO DIA ${dia}/${mes}/${ano}`,
          });
        }

        addToast({
          type: 'success',
          title: 'Parabéns',
          description: 'Agendamento cadastrado com sucesso',
        });

        onClose();
      }
    } catch (error) {
      addToast(returnAxiosError(error));

      return;
    }
  }, [
    addToast,
    data,
    categoria,
    dataParametro,
    horaIni,
    horaFim,
    paciente,
    local,
    obs,
    profissional,
    agendamentoParametro,
    validacoesAgendamento,
    onClose,
    verifyAccess,
    esperaParametro,
  ]);

  useEffect(() => {
    atualizaDados();
    defineHorarios();
    setIntervalo(lerConfig('INTERVALO'));
  }, [atualizaDados, defineHorarios, lerConfig]);

  useEffect(() => {
    if (agendamentoParametro) {
      setPaciente(
        agendamentoParametro.paciente ? agendamentoParametro.paciente.id : '',
      );
      setCategoria(
        agendamentoParametro.categoria ? agendamentoParametro.categoria.id : '',
      );
      setLocal(agendamentoParametro.local_id);
      setObs(
        agendamentoParametro.observacoes
          ? agendamentoParametro.observacoes
          : '',
      );
      setAgendado(true);
      aoAlterarCategoria(
        agendamentoParametro.categoria ? agendamentoParametro.categoria.id : '',
      );
      setTimeout(() => {
        setHoraIni(horaParametro);
        setHoraFim(
          adicionarMinutos(
            horaParametro,
            agendamentoParametro?.categoria?.metade_intervalo && intervalo
              ? String(Number(intervalo) / 2)
              : intervalo,
          ),
        );
      }, 1200);
    }
  }, [
    agendamentoParametro,
    aoAlterarCategoria,
    horaParametro,
    lerConfig,
    intervalo,
  ]);

  useEffect(() => {
    if (esperaParametro) {
      setPaciente(esperaParametro.paciente.id);
      setCategoria(esperaParametro.categoria.id);
    }
  }, [esperaParametro]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  useEffect(() => {
    setHoraIni(horaParametro);
    setHoraFim(adicionarMinutos(horaParametro, intervalo));
  }, [horaParametro, intervalo]);

  return loading ? (
    <CirculoCarregandoDiv>
      <CirculoCarregando />
    </CirculoCarregandoDiv>
  ) : (
    <Container>
      <h1>{'Agendamento'}</h1>

      <span>
        {esperaParametro && (
          <InputData
            variant="outlined"
            margin="normal"
            id="data"
            label="Data"
            name="data"
            type="date"
            InputLabelProps={{
              shrink: true,
            }}
            value={data}
            onChange={aoAlterarData}
            error={dataErro !== ''}
            helperText={dataErro}
          />
        )}

        <ComboboxComFiltro
          opcoes={profissionais}
          value={profissional}
          label="Profissional"
          onChange={aoAlterarProfissional}
          error={profissionalErro !== ''}
          helperText={profissionalErro}
          size={5}
          temMargin={true}
          //disabled={!esperaParametro}
        />

        <ComboboxComFiltro
          opcoes={categorias}
          value={categoria}
          label="Categoria"
          onChange={aoAlterarCategoria}
          error={categoriaErro !== ''}
          helperText={categoriaErro}
          size={4}
          temMargin={true}
        />

        <ComboboxComFiltro
          opcoes={horarios}
          value={horaIni}
          label="Hora Início"
          onChange={aoAlterarHoraIni}
          error={horaIniErro !== ''}
          helperText={horaIniErro}
          size={1.5}
          temMargin={true}
          disabled={agendado}
        />

        <ComboboxComFiltro
          opcoes={horarios}
          value={horaFim}
          label="Hora Fim"
          onChange={aoAlterarHoraFim}
          error={horaFimErro !== ''}
          helperText={horaFimErro}
          size={1.5}
          disabled={agendado}
        />
      </span>

      <ContainerButton>
        <ComboboxComFiltro
          opcoes={pacientes}
          value={paciente}
          label="Paciente"
          onChange={aoAlterarPaciente}
          error={pacienteErro !== ''}
          helperText={pacienteErro}
          disabled={esperaParametro ? true : false}
        />

        {!esperaParametro && (
          <AddPacienteButton
            title="Cadastrar Paciente"
            onClick={handleOpenDialog}
            disabled={esperaParametro ? true : false}
          >
            <FiPlus />
          </AddPacienteButton>
        )}

        <Dialog isOpen={isDialogOpen} onClose={handleCloseDialog}>
          <Paciente
            externa={true}
            pacienteParametro={esperaParametro?.paciente}
            onClose={handleCloseDialog}
          />
        </Dialog>
      </ContainerButton>

      <span>
        <ComboboxComFiltro
          opcoes={locais}
          value={local}
          label="Local Atendimento"
          onChange={aoAlterarLocal}
          error={localErro !== ''}
          helperText={localErro}
          size={1}
          temMargin={true}
        />

        <InputObs
          variant="outlined"
          margin="normal"
          id="obs"
          label="Observações"
          name="obs"
          value={obs}
          onChange={aoAlterarObs}
          style={{ flex: 2 }}
        />
      </span>

      <BotoesContainer>
        <BotaoSalvar onClick={aoSalvar}>
          <FiSave />
          Salvar
        </BotaoSalvar>

        {!esperaParametro && (
          <BotaoLimpar onClick={aoLimpar}>
            <FiRefreshCw />
            Limpar
          </BotaoLimpar>
        )}

        {agendamentoParametro && !esperaParametro && (
          <BotaoExcluir
            onClick={() => {
              setModalOpen(true);
            }}
          >
            <FiTrash2 />
            Excluir
          </BotaoExcluir>
        )}
      </BotoesContainer>

      <ConfirmationModal
        open={isModalOpen}
        title="Confirmar Exclusão"
        message="Informe o motivo para excluir esse agendamento"
        exigeMotivo={true}
        onConfirm={motivo => {
          setMotivoExclusao(motivo);
          handleExcluirAgendamento(motivo);
        }}
        onCancel={() => setModalOpen(false)}
      />

      <ConfirmationModal
        open={excluiTodosOpen}
        title="Exclusão de Agendamento"
        message="Deseja excluir o agendamento atual da recorrência ou desse horário pra frente?"
        exigeMotivo={false}
        textoCancelar="Atual"
        textoConfirmar="A partir dessa"
        onConfirm={async () => {
          try {
            setLoading(true);

            await api.post(
              `/agendamentos/excluir/${agendamentoParametro?.id}/${true}`,
              {
                motivo: motivoExclusao.toUpperCase(),
                data: agendamentoParametro?.data_criacao,
              },
            );

            setLoading(false);
          } catch (error) {
            addToast(returnAxiosError(error));

            return;
          }

          addToast({
            type: 'success',
            title: 'Removido',
            description: 'Agendamento excluido',
          });

          setTimeout(() => onClose(), 1000);
        }}
        onCancel={async () => {
          try {
            setLoading(true);

            await api.post(
              `/agendamentos/excluir/${agendamentoParametro?.id}/${false}`,
              {
                motivo: motivoExclusao.toUpperCase(),
                data: agendamentoParametro?.data_criacao,
              },
            );

            setLoading(false);
          } catch (error) {
            addToast(returnAxiosError(error));

            return;
          }

          addToast({
            type: 'success',
            title: 'Removido',
            description: 'Agendamento excluido',
          });

          setTimeout(() => onClose(), 1000);
        }}
        onClose={() => setExcluiTodosOpen(false)}
      />

      <ConfirmationModal
        open={alteraTodosOpen}
        title="Alteração de Agendamento"
        message="Deseja alterar toda a recorrência daqui pra frente? (PROCESSO DEMORADO, AGUARDE A FINALIZAÇÃO)"
        exigeMotivo={false}
        textoCancelar="Todos"
        textoConfirmar="Atual"
        onConfirm={async () => {
          const ano = dataParametro.getFullYear();
          const mes = String(dataParametro.getMonth() + 1).padStart(2, '0');
          const dia = String(dataParametro.getDate()).padStart(2, '0');
          const dataFormatada = `${ano}-${mes}-${dia}`;

          try {
            setLoading(true);

            await api.put(`/agendamentos/${agendamentoParametro?.id}`, {
              categoria_id: categoria,
              hora: horaIni,
              observacoes: obs,
              data_criacao: dataFormatada,
              profissional_id: profissional,
              paciente_id: paciente,
              inutilizado: false,
              local_id: local,
            });

            addToast({
              type: 'success',
              title: 'Parabéns',
              description: 'Agendamento cadastrado com sucesso',
            });

            onClose();
          } catch (error) {
            addToast(returnAxiosError(error));
            setAlteraTodosOpen(false);
          }

          setLoading(false);
        }}
        onCancel={async () => {
          setLoading(true);

          try {
            await api.put(
              `/agendamentos/recorrencia/${agendamentoParametro?.id}`,
              {
                categoria_id: categoria,
                hora: horaIni,
                observacoes: obs,
                profissional_id: profissional,
                paciente_id: paciente,
                local_id: local,
              },
            );

            addToast({
              type: 'success',
              title: 'Parabéns',
              description: 'Agendamento cadastrado com sucesso',
            });

            onClose();
          } catch (error) {
            addToast(returnAxiosError(error));
            setAlteraTodosOpen(false);
          }

          setLoading(false);
        }}
        onClose={() => setAlteraTodosOpen(false)}
      />
    </Container>
  );
};

export default Agendamento;
